// src/modals/ClientMetricsModal.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Grid,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

// Helper to format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return '$0';
  
  const numValue = typeof value === 'string' ? 
    parseFloat(value.replace(/[$,]/g, '')) : 
    Number(value);
  
  if (isNaN(numValue)) return '$0';
  
  return `$${Math.round(numValue).toLocaleString('en-US')}`;
};

const ClientMetricsModal = ({ 
  open, 
  onClose, 
  clientName, 
  clientId, 
  metricsData = {} 
}) => {
  const {
    total_revenue = 0,
    active_projects = 0,
    total_projects = 0,
    avg_project_value = 0,
    client_satisfaction = null
  } = metricsData;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          color: 'text.primary'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
          {clientName} - Detailed Metrics
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Comprehensive performance metrics and analytics for {clientName}.
          </Typography>
          
          <Grid container spacing={2}>
            {/* Total Revenue */}
            <Grid item xs={12} sm={6}>
              <Paper sx={{ 
                p: 2, 
                bgcolor: 'rgba(3, 218, 198, 0.1)', 
                border: '1px solid rgba(3, 218, 198, 0.3)',
                borderRadius: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AttachMoneyIcon sx={{ fontSize: '1.5rem', color: '#03DAC6', mr: 1 }} />
                  <Typography variant="h6" sx={{ 
                    fontSize: '1rem', 
                    color: '#03DAC6',
                    fontWeight: 'bold'
                  }}>
                    Total Revenue
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ 
                  fontSize: '1.8rem', 
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  {formatCurrency(total_revenue)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Lifetime revenue from all projects
                </Typography>
              </Paper>
            </Grid>

            {/* Active Projects */}
            <Grid item xs={12} sm={6}>
              <Paper sx={{ 
                p: 2, 
                bgcolor: 'rgba(100, 181, 246, 0.1)', 
                border: '1px solid rgba(100, 181, 246, 0.3)',
                borderRadius: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <BusinessCenterIcon sx={{ fontSize: '1.5rem', color: '#64B5F6', mr: 1 }} />
                  <Typography variant="h6" sx={{ 
                    fontSize: '1rem', 
                    color: '#64B5F6',
                    fontWeight: 'bold'
                  }}>
                    Active Projects
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ 
                  fontSize: '1.8rem', 
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  {active_projects}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Currently active engagements
                </Typography>
              </Paper>
            </Grid>

            {/* Average Project Value */}
            <Grid item xs={12} sm={6}>
              <Paper sx={{ 
                p: 2, 
                bgcolor: 'rgba(255, 207, 68, 0.1)', 
                border: '1px solid rgba(255, 207, 68, 0.3)',
                borderRadius: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon sx={{ fontSize: '1.5rem', color: '#FFCF44', mr: 1 }} />
                  <Typography variant="h6" sx={{ 
                    fontSize: '1rem', 
                    color: '#FFCF44',
                    fontWeight: 'bold'
                  }}>
                    Average Project Value
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ 
                  fontSize: '1.8rem', 
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  {formatCurrency(avg_project_value)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Mean value per project
                </Typography>
              </Paper>
            </Grid>

            {/* Total Projects */}
            <Grid item xs={12} sm={6}>
              <Paper sx={{ 
                p: 2, 
                bgcolor: 'rgba(207, 102, 121, 0.1)', 
                border: '1px solid rgba(207, 102, 121, 0.3)',
                borderRadius: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <BusinessCenterIcon sx={{ fontSize: '1.5rem', color: '#CF6679', mr: 1 }} />
                  <Typography variant="h6" sx={{ 
                    fontSize: '1rem', 
                    color: '#CF6679',
                    fontWeight: 'bold'
                  }}>
                    Total Projects
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ 
                  fontSize: '1.8rem', 
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  {total_projects}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All-time project count
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {client_satisfaction && (
            <Box sx={{ mt: 3 }}>
              <Paper sx={{ 
                p: 2, 
                bgcolor: 'rgba(133, 97, 197, 0.1)', 
                border: '1px solid rgba(133, 97, 197, 0.3)',
                borderRadius: 2
              }}>
                <Typography variant="h6" sx={{ 
                  fontSize: '1rem', 
                  color: '#8561c5',
                  fontWeight: 'bold',
                  mb: 1
                }}>
                  Client Satisfaction Score
                </Typography>
                <Typography variant="h4" sx={{ 
                  fontSize: '1.8rem', 
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  {client_satisfaction.toFixed(1)}/5.0
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average satisfaction across all projects
                </Typography>
              </Paper>
            </Box>
          )}
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            This modal will be enhanced with charts, trends, and detailed analytics
            including project performance over time, revenue forecasting, and relationship insights.
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ClientMetricsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clientName: PropTypes.string,
  clientId: PropTypes.string,
  metricsData: PropTypes.shape({
    total_revenue: PropTypes.number,
    active_projects: PropTypes.number,
    total_projects: PropTypes.number,
    avg_project_value: PropTypes.number,
    client_satisfaction: PropTypes.number
  })
};

export default ClientMetricsModal;
